import { useCallback } from 'react';

import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import { getListingDetailsQueryKey } from '@zg-rentals/zillow-rental-manager/hooks/useGetListingDetails';

import { AMENITY_LOCATION_BY_FIELD, LAUNDRY_VALUE_BY_FIELD } from '../../constants/KeyFeaturesConstants';

import type {
  GetUserPropertyDetailsAmenitiesGroupWebDto,
  GetUserPropertyDetailsFloorPlanUnitWebDto,
  GetUserPropertyDetailsFloorPlanWebDto,
  GetUserPropertyDetailsResponseWebDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import type { AmenityLocation } from '../../constants/KeyFeaturesConstants';

/** `field` is the API field name, so a `/multi` soft error can drop the patch it rejected. */
export type ListingDetailsPatch = {
  field: string;
  apply: (cached: GetUserPropertyDetailsResponseWebDto) => GetUserPropertyDetailsResponseWebDto;
};

// Patches for a payload whose keys already name the `/details` fields they update
export const flatListingDetailsPatches = (variables: unknown): Array<ListingDetailsPatch> => {
  const postData = (variables as { postData?: Record<string, unknown> } | undefined)?.postData;

  if (!postData) {
    return [];
  }

  return Object.entries(postData)
    .filter(([field]) => field !== 'listingAlias')
    .map(([field, value]) => ({
      field,
      apply: (cached: GetUserPropertyDetailsResponseWebDto) =>
        field in cached ? { ...cached, [field]: value } : cached,
    }));
};

type ArrayFieldOf<T> = { [K in keyof T]: T[K] extends Array<unknown> ? K : never }[keyof T];
type ElementOf<T> = T extends Array<infer E> ? E : never;

// Floor plan and unit ids share the `floorPlanId` payload key, so a miss is a bug, not a no-op.
export const setFloorPlanField = <K extends keyof GetUserPropertyDetailsFloorPlanWebDto>(
  floorPlanId: string | undefined,
  field: K,
  // Generated DTOs omit null, but a cleared field saves as null.
  value: GetUserPropertyDetailsFloorPlanWebDto[K] | null,
): ListingDetailsPatch => ({
  field,
  apply: (cached) => {
    if (!cached.floorPlans?.some((floorPlan) => floorPlan.id === floorPlanId)) {
      logError({
        error: new Error('No floor plan matched the id being patched'),
        context: { details: 'setFloorPlanField', field },
      });
      return cached;
    }

    return {
      ...cached,
      floorPlans: cached.floorPlans.map((floorPlan) =>
        floorPlan.id === floorPlanId ? { ...floorPlan, [field]: value } : floorPlan,
      ),
    };
  },
});

// A unit sits under its floor plan, or at the top level when it has none.
export const setUnitField = <K extends keyof GetUserPropertyDetailsFloorPlanUnitWebDto>(
  unitId: string | undefined,
  field: K,
  value: GetUserPropertyDetailsFloorPlanUnitWebDto[K],
): ListingDetailsPatch => ({
  field,
  apply: (cached) => {
    const holdsUnit = (units?: Array<GetUserPropertyDetailsFloorPlanUnitWebDto>) =>
      Boolean(units?.some((unit) => unit.id === unitId));
    const setOnUnits = (units: Array<GetUserPropertyDetailsFloorPlanUnitWebDto>) =>
      units.map((unit) => (unit.id === unitId ? { ...unit, [field]: value } : unit));

    if (!cached.floorPlans?.some((floorPlan) => holdsUnit(floorPlan.units)) && !holdsUnit(cached.units)) {
      logError({
        error: new Error('No unit matched the id being patched'),
        context: { details: 'setUnitField', field },
      });
      return cached;
    }

    return {
      ...cached,
      floorPlans: cached.floorPlans?.map((floorPlan) =>
        holdsUnit(floorPlan.units) ? { ...floorPlan, units: setOnUnits(floorPlan.units) } : floorPlan,
      ),
      units: holdsUnit(cached.units) ? setOnUnits(cached.units) : cached.units,
    };
  },
});

// MF `/details` omits `amenities` despite the DTO, so the helpers keep their runtime guards.
type Amenities = GetUserPropertyDetailsResponseWebDto['amenities'];
type BuildingAttributes = GetUserPropertyDetailsResponseWebDto['buildingAttributes'];

const hasGroupEntry = (amenities: Amenities, fieldName: string) =>
  [...(amenities?.interior ?? []), ...(amenities?.property ?? [])].some((group) =>
    group.values?.some((value) => value.fieldName === fieldName),
  );

const selectInGroups = (amenities: Amenities, fieldName: string, isSelected: boolean): Amenities => {
  const selectIn = (groups: Array<GetUserPropertyDetailsAmenitiesGroupWebDto> = []) =>
    groups.map((group) => ({
      ...group,
      values: (group.values ?? []).map((value) => (value.fieldName === fieldName ? { ...value, isSelected } : value)),
    }));

  return amenities && { ...amenities, interior: selectIn(amenities.interior), property: selectIn(amenities.property) };
};

const setAttribute = (
  attributes: BuildingAttributes,
  { key, category }: AmenityLocation,
  isSelected: boolean,
): BuildingAttributes =>
  category
    ? {
        ...attributes,
        [category]: { ...(attributes[category as keyof BuildingAttributes] as object), [key]: isSelected },
      }
    : { ...attributes, [key]: isSelected };

// The server derives `laundry` from four exclusive flags, so selecting one clears the rest and
// clearing one on its own changes nothing.
const selectLaundry = (
  cached: GetUserPropertyDetailsResponseWebDto,
  fieldName: string,
): GetUserPropertyDetailsResponseWebDto => ({
  ...cached,
  laundry: LAUNDRY_VALUE_BY_FIELD[fieldName],
  amenities: Object.keys(LAUNDRY_VALUE_BY_FIELD).reduce(
    (amenities, flag) => selectInGroups(amenities, flag, flag === fieldName),
    cached.amenities,
  ),
});

// The MF accordion reads an amenity from buildingAttributes and the posting path reads it from
// the groups, so each copy is written when it exists.
export const setAmenityField = (fieldName: string, isSelected: boolean): ListingDetailsPatch => ({
  field: fieldName,
  apply: (cached) => {
    if (LAUNDRY_VALUE_BY_FIELD[fieldName]) {
      return isSelected ? selectLaundry(cached, fieldName) : cached;
    }

    const location = AMENITY_LOCATION_BY_FIELD[fieldName];
    const inAttributes = Boolean(location && cached.buildingAttributes);
    const inGroups = hasGroupEntry(cached.amenities, fieldName);

    if (!inAttributes && !inGroups) {
      logError({
        error: new Error('No amenity location matched the field being patched'),
        context: { details: 'setAmenityField', fieldName },
      });
      return cached;
    }

    return {
      ...cached,
      buildingAttributes: inAttributes
        ? setAttribute(cached.buildingAttributes, location, isSelected)
        : cached.buildingAttributes,
      amenities: inGroups ? selectInGroups(cached.amenities, fieldName, isSelected) : cached.amenities,
    };
  },
});

// Each key of an amenity payload names the field it saves, so the patch is read from the payload.
export const amenityPatches = (fields: object): Array<ListingDetailsPatch> =>
  Object.entries(fields)
    .filter(([field]) => field !== 'listingAlias')
    .map(([field, value]) => setAmenityField(field, Boolean(value)));

export const removeListingDetailsItem = <
  K extends ArrayFieldOf<GetUserPropertyDetailsResponseWebDto>,
  I extends keyof ElementOf<GetUserPropertyDetailsResponseWebDto[K]>,
>(
  field: K,
  idKey: I,
  id: ElementOf<GetUserPropertyDetailsResponseWebDto[K]>[I],
): ListingDetailsPatch => ({
  field: String(field),
  apply: (cached) => {
    const items = cached[field] as Array<ElementOf<GetUserPropertyDetailsResponseWebDto[K]>> | undefined;

    return items ? { ...cached, [field]: items.filter((item) => item[idKey] !== id) } : cached;
  },
});

export const useApplyListingDetailsPatches = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (listingAlias: string, patches: Array<ListingDetailsPatch>) => {
      if (!listingAlias || patches.length === 0) {
        return;
      }

      queryClient.setQueryData<GetUserPropertyDetailsResponseWebDto>(
        getListingDetailsQueryKey(listingAlias),
        // Leave an absent entry absent, rather than seeding a listing with only these fields.
        (cached) => (cached ? patches.reduce((next, patch) => patch.apply(next), cached) : cached),
      );
    },
    [queryClient],
  );
};
